import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { getDashboardData } from 'api/cantrack/getDashboardData';
import { dashboardKeys } from './dashboardQueryKeys';
import type {
  DashboardData,
  DashboardStats,
  MyCaseSummary,
  RecentActivityItem,
  PinnedCase,
  TodayEntry,
  DailyWorkPlanState,
} from 'containers/pages/cantrackDashboard/types';

function useDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userInfo } = useSelector(selectUserInfo) as any;

  const userId = userInfo?.userId ?? '';
  const contactId = userInfo?.contactId ?? '';
  const employeeId = userInfo?.employeeId ?? '';
  const userName = userInfo?.firstName ?? '';

  const {
    data,
    isLoading: loading,
    error: queryError,
  } = useQuery<DashboardData>({
    queryKey: dashboardKeys.data(userId),
    queryFn: () => getDashboardData(userId, contactId, employeeId),
    staleTime: 30_000,
    enabled: !!userId,
  });

  const stats: DashboardStats = useMemo(() => {
    if (!data) return { openCases: 0, entriesToday: 0, hoursToday: 0 };
    return {
      openCases: data.myCases.length,
      entriesToday: data.todayEntries.length,
      hoursToday: Math.round(
        data.todayEntries.reduce(
          (sum, e) => sum + (e.timeRegular || 0) + (e.timeOver || 0),
          0,
        ) * 100,
      ) / 100,
    };
  }, [data]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: dashboardKeys.data(userId) });
  }, [queryClient, userId]);

  const onCaseClick = useCallback(
    (caseId: number) => {
      navigate(`/cantrack/cases/${caseId}`);
    },
    [navigate],
  );

  return {
    state: {
      myCases: (data?.myCases ?? []) as MyCaseSummary[],
      recentActivity: (data?.recentActivity ?? []) as RecentActivityItem[],
      pinnedCases: (data?.pinnedCases ?? []) as PinnedCase[],
      todayEntries: (data?.todayEntries ?? []) as TodayEntry[],
      dailyWorkPlan: (data?.dailyWorkPlan ?? null) as DailyWorkPlanState | null,
      stats,
      loading,
      error: queryError ? String(queryError) : null,
      userName,
    },
    handler: {
      refresh,
      onCaseClick,
    },
  };
}

export default useDashboard;
